import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, MessageSquare, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ClarificationDialog Component
 * 
 * Modal dialog for principal party to request clarification from secondary party.
 * Allows principal party to provide a reason for requesting reconfirmation.
 * 
 * Requirements: 6.1, 6.4
 */
export default function ClarificationDialog({ open, onOpenChange, agreement, onSuccess }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(t('loan.clarification.reason_placeholder'));
      return;
    }

    if (reason.trim().length < 10) {
      toast.error('Please provide a more detailed reason (at least 10 characters)');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/loans/${agreement.id}/clarification-request`, {
        reason: reason.trim(),
      });

      setSubmitted(true);
      toast.success(t('loan.clarification.request_success'));
      
      // Wait a moment to show success state, then close and refresh
      setTimeout(() => {
        setReason('');
        setSubmitted(false);
        onOpenChange(false);
        onSuccess?.();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send clarification request');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason('');
      setSubmitted(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            {t('loan.clarification.request_title')}
          </DialogTitle>
          <DialogDescription>
            {t('loan.clarification.reconfirm_message')}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <>
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-900">Neutral Witness</span>
                </div>
                <p className="text-xs text-amber-700">
                  Pakka acts as a neutral witness. This request will be recorded in the timeline without judgment. The secondary party will be able to submit a new video confirmation.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason" className="text-sm font-medium">
                  {t('loan.clarification.reason_label')}
                </Label>
                <Textarea
                  id="reason"
                  placeholder={t('loan.clarification.reason_placeholder')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={5}
                  className="resize-none"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Be specific about what you'd like the secondary party to clarify in their reconfirmation video.
                </p>
              </div>

              <div className="border-l-4 border-primary pl-4 py-2">
                <p className="text-sm font-medium">What happens next?</p>
                <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                  <li>• The secondary party will be notified of your request</li>
                  <li>• They can submit a new video confirmation</li>
                  <li>• Both videos will be preserved in the timeline</li>
                  <li>• You can then review and record your confirmation</li>
                </ul>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !reason.trim()}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('loan.clarification.request_button')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-8 space-y-4">
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-center space-y-1">
                <p className="font-medium">{t('loan.clarification.request_success')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('loan.clarification.pending_message')}
                </p>
              </div>
            </div>
            <div className="bg-primary/5 border border-primary/10 p-4 rounded-sm">
              <p className="text-xs text-muted-foreground">
                <strong>{t('loan.clarification.clarification_reason')}:</strong> {reason}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
